import moment from "moment";
import Icon from "@mdi/react";
import { useMemo, useRef, useState } from "react";
import {
    mdiArrowRightThin,
    mdiFile, mdiPencil, mdiPlus,
} from "@mdi/js";

import { useSSRHook } from "ssr-hook";
import { useUser } from "../User";
import { FileIcon, Header, HeaderProps, InputDate, Loading, Remove } from "../components";
import { api } from "../services/api";

import { Download, DownloadFolder } from "../../types";
import { groupBy } from "lodash";
import Modal from "react-responsive-modal";
import Footer from "../components/Footer";
import { getFileName, toBase64, useHead } from "../services/common";

type DownloadsProps = {
    title: string,
    folder: DownloadFolder,
    background: HeaderProps["background"],
}

const emptyNew: Download = {
    _id: "",
    name: "",
    folder: "board",
    date: moment().format("YYYY-MM-DD"),
    isArchived: false,
    dateOfRemoval: null,
    file: {
        type: "EASY_DB_FILE",
        url: "",
    },
}

export default function Downloads({ title, folder, background }: DownloadsProps) {
    const user = useUser();

    const [downloads, error, isLoading, reload] = useSSRHook<Download[]>(`/api/downloads?folder=${encodeURIComponent(folder)}`);

    useHead(
        title,
        title,
    );

    const [category, setCategory] = useState("Aktivní");

    const inputRef = useRef<HTMLInputElement>(null);
    const [editDownload, setEditDownload] = useState<null | Download>(null);

    const [message, setMessage] = useState("");

    const categories = useMemo(() => {
        if (!downloads) return {};

        return groupBy(downloads, d => d.isArchived ? `Archiv ${moment(d.date).format("YYYY")}` : "Aktivní");
    }, [downloads]);


    async function handleSave() {
        if (!editDownload) return;

        if (editDownload.isArchived && !editDownload.dateOfRemoval)
            editDownload.dateOfRemoval = moment().format("YYYY-MM-DD");

        if (editDownload._id === "") {
            const [, error] = await api.addDownload(editDownload);
            if (error) {
                setMessage("Přidání souboru selhalo");
                return;
            }
            setMessage("Soubor přidán");
        } else {
            const [, error] = await api.updateDownload(editDownload);
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Soubor úspěšně uložen")
        }

        reload();
        setEditDownload(null);
    }

    async function handleRemove(downloadId: string) {
        await api.removeDownload({ downloadId });
        reload();
    }

    return (
        <>
            <Header title={title} background={background} />

            <main>
                <div className="content-page">
                    <h1 className="hidden">{title}</h1>
                    <div className="content-split">
                        <section className="left">
                            <h2>
                                {folder === "documents" ? "Dokumenty" : "Soubory"}
                                {folder === "board" && <p className="info">Originály povinně zveřejňovaných dokumentů jsou uložené k nahlédnutí na adrese kanceláře organizace.</p>}
                                {user.isAdmin && <span style={{ marginLeft: "20px" }} className="ticket clickable" onClick={() => inputRef.current?.click()}>
                                    <Icon path={mdiPlus} size={1} /> Přidat soubor
                                </span>}
                            </h2>

                            {user.isAdmin && <>

                                {message && <p className="alert">{message}</p>}

                                <input
                                    className="hidden"
                                    ref={inputRef}
                                    type="file"
                                    placeholder="Nahrát soubor"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0] as File;
                                        if (!file) return;

                                        const url = await toBase64(file);

                                        setMessage("");
                                        setEditDownload({
                                            ...(editDownload || emptyNew),
                                            name: file.name,
                                            folder,
                                            file: {
                                                type: "EASY_DB_FILE",
                                                url,
                                            },
                                        });
                                    }}
                                />

                                <Modal
                                    open={editDownload !== null}
                                    onClose={() => setEditDownload(null)}
                                    center
                                    styles={{ modal: { width: "500px" } }}
                                >
                                    {message && <p className="alert">{message}</p>}
                                    {editDownload && <>
                                        <h2>Přidat soubor</h2>
                                        <button onClick={() => inputRef.current?.click()}>
                                            <Icon path={mdiFile} size={1} />Nahrát jiný soubor
                                        </button>
                                        <p>Soubor může mít maximálně 10MB!</p>
                                        <label>Datum zveřejnění</label>
                                        <InputDate
                                            value={editDownload.date}
                                            onChange={(date) => setEditDownload({ ...editDownload, date })}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Název"
                                            value={editDownload.name}
                                            onChange={(e) =>
                                                setEditDownload({ ...editDownload, name: e.target.value })}
                                        />
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={editDownload.isArchived}
                                                onChange={(e) => setEditDownload({
                                                    ...editDownload,
                                                    isArchived: e.target.checked,
                                                    dateOfRemoval: e.target.checked ? moment().format("YYYY-MM-DD") : null,
                                                })}
                                            />
                                            Archivovaný
                                        </label>
                                        {editDownload.isArchived && <>
                                            <br />
                                            <label>Datum sejmutí</label>
                                            <InputDate
                                                value={editDownload.dateOfRemoval || moment().format("YYYY-MM-DD")}
                                                onChange={(dateOfRemoval) => setEditDownload({ ...editDownload, dateOfRemoval })}
                                            />
                                        </>}
                                        <div className="buttons">
                                            <button onClick={handleSave}>Uložit soubor</button>
                                        </div>
                                    </>}
                                </Modal>
                            </>}

                            {isLoading && <Loading />}
                            {error && (
                                <p className="alert">
                                    Soubory se bohužel nepodařili načíst 🙁
                                </p>
                            )}
                            {downloads && <ul className="list-download">
                                {!categories[category] && <li>
                                    <a>Zatím zde nejsou žádné soubory ke stažení.</a>
                                </li>}
                                {categories[category]?.map((d) => <li key={d._id}>
                                    <FileIcon url={d.file.url} size={2} />
                                    <span className="name">{d.name}</span>
                                    <span className="date">
                                        {!d.dateOfRemoval && moment(d.date).format("LL")}
                                        {d.dateOfRemoval && <>
                                            Zveřejněno: {moment(d.date).format("LL")}<br />
                                            Sejmuto: {moment(d.dateOfRemoval).format("LL")}
                                        </>}
                                    </span>
                                    <a className="button black" href={d.file.url} target="__blank" download={getFileName(d.name, d.file.url)}>
                                        Stáhnout
                                    </a>
                                    {user.isAdmin && <>
                                        <button className="icon" onClick={() => setEditDownload(d)}>
                                            <Icon path={mdiPencil} size={1} />
                                        </button>
                                        <Remove onRemove={() => handleRemove(d._id)} />
                                    </>}
                                </li>)}
                            </ul>}
                        </section>

                        <section className="right">
                            <h3>Archiv</h3>
                            {isLoading && <Loading />}
                            {downloads && <ul className="list-archive">
                                {Object.keys(categories).length === 0 && <li>
                                    Zatím zde není žádný archiv.
                                </li>}
                                {Object.keys(categories)
                                    .sort((a, b) => {
                                        if (a === "Aktivní") return -1;
                                        if (b === "Aktivní") return 1;
                                        return b.localeCompare(a);
                                    })
                                    .map(c => <li key={c}>
                                        <a onClick={() => setCategory(c)} className={c === category ? "active" : ""}>
                                            <span>{c}</span>
                                            <Icon path={mdiArrowRightThin} size={1} />
                                        </a>
                                    </li>)}
                            </ul>}
                        </section>
                    </div>
                </div>
            </main>

            <Footer />
        </>
    );
}
